/**
 * Hardware catalog — deterministic mechanical-dimension reference.
 *
 * Used by intakeBuilderDefaults, renderer helpers, validation hooks, and
 * drawing callouts. Agent prompts may reference catalog-backed sizes; the
 * renderer and builders own the final dimensions.
 *
 * Mirrored by functions/src/shared/hardwareCatalog.ts. The two files MUST stay
 * byte-identical; a sync test (shared/__tests__/hardwareCatalog.test.ts)
 * enforces this.
 *
 * Sources for nominal values:
 *   - ISO 273 (medium-fit clearance holes for metric screws)
 *   - ISO 4762 (socket-head cap screw dimensions)
 *   - ISO 7045/7046 (pan/countersunk machine screws)
 *   - ISO 4032 (hex nut dimensions)
 *   - SKF/NSK published dimensions for common bearings
 *   - Voltive/E-Z LOK heat-set insert datasheets (typical 3D-print values)
 */

/** Metric screw size — nominal thread diameter in mm. */
export type MetricScrewSize = 'M2' | 'M2.5' | 'M3' | 'M4' | 'M5' | 'M6' | 'M8';

/** Per metric size: clearance hole, tap drill, pilot drill, head dimensions
 *  for socket/pan/countersunk heads, and recommended counterbore/countersink. */
export type MetricScrewSpec = {
  size: MetricScrewSize;
  /** Nominal thread diameter, mm. */
  nominal: number;
  /** ISO 273 medium-fit clearance hole, mm. */
  clearanceHole: number;
  /** Drill size for cutting threads with the matching tap. */
  tapDrill: number;
  /** Pilot/lead-in for self-tapping use, mm. */
  pilotDrill: number;
  /** Socket-head cap screw head OD, mm. */
  socketHeadDiameter: number;
  /** Socket-head cap screw head height, mm. */
  socketHeadHeight: number;
  /** Pan-head OD, mm (nominal — varies slightly by standard). */
  panHeadDiameter: number;
  /** Pan-head height, mm. */
  panHeadHeight: number;
  /** Countersunk (flat-head) OD at top of taper, mm. */
  countersunkHeadDiameter: number;
  /** Countersink half-angle, degrees, from axis. ISO uses 90° included = 45° half-angle. */
  countersinkHalfAngle: 45;
  /** Recommended counterbore depth = head height + 0.5mm clearance. */
  recommendedCounterboreDepth: number;
};

export const METRIC_SCREWS: Record<MetricScrewSize, MetricScrewSpec> = {
  M2:   { size: 'M2',   nominal: 2.0, clearanceHole: 2.4, tapDrill: 1.6, pilotDrill: 1.5,  socketHeadDiameter: 3.8, socketHeadHeight: 2.0, panHeadDiameter: 4.0, panHeadHeight: 1.6, countersunkHeadDiameter: 3.8, countersinkHalfAngle: 45, recommendedCounterboreDepth: 2.5 },
  'M2.5': { size: 'M2.5', nominal: 2.5, clearanceHole: 2.9, tapDrill: 2.05, pilotDrill: 1.9, socketHeadDiameter: 4.5, socketHeadHeight: 2.5, panHeadDiameter: 5.0, panHeadHeight: 2.1, countersunkHeadDiameter: 4.7, countersinkHalfAngle: 45, recommendedCounterboreDepth: 3.0 },
  M3:   { size: 'M3',   nominal: 3.0, clearanceHole: 3.4, tapDrill: 2.5, pilotDrill: 2.4,  socketHeadDiameter: 5.5, socketHeadHeight: 3.0, panHeadDiameter: 5.6, panHeadHeight: 2.4, countersunkHeadDiameter: 6.3, countersinkHalfAngle: 45, recommendedCounterboreDepth: 3.5 },
  M4:   { size: 'M4',   nominal: 4.0, clearanceHole: 4.5, tapDrill: 3.3, pilotDrill: 3.2,  socketHeadDiameter: 7.0, socketHeadHeight: 4.0, panHeadDiameter: 8.0, panHeadHeight: 3.1, countersunkHeadDiameter: 9.2, countersinkHalfAngle: 45, recommendedCounterboreDepth: 4.5 },
  M5:   { size: 'M5',   nominal: 5.0, clearanceHole: 5.5, tapDrill: 4.2, pilotDrill: 4.1,  socketHeadDiameter: 8.5, socketHeadHeight: 5.0, panHeadDiameter: 9.5, panHeadHeight: 3.8, countersunkHeadDiameter: 11.0, countersinkHalfAngle: 45, recommendedCounterboreDepth: 5.5 },
  M6:   { size: 'M6',   nominal: 6.0, clearanceHole: 6.6, tapDrill: 5.0, pilotDrill: 4.8,  socketHeadDiameter: 10.0, socketHeadHeight: 6.0, panHeadDiameter: 12.0, panHeadHeight: 4.6, countersunkHeadDiameter: 13.3, countersinkHalfAngle: 45, recommendedCounterboreDepth: 6.5 },
  M8:   { size: 'M8',   nominal: 8.0, clearanceHole: 9.0, tapDrill: 6.8, pilotDrill: 6.5,  socketHeadDiameter: 13.0, socketHeadHeight: 8.0, panHeadDiameter: 16.0, panHeadHeight: 6.0, countersunkHeadDiameter: 17.7, countersinkHalfAngle: 45, recommendedCounterboreDepth: 8.5 },
};

/** Hex/square nut across-flats and thickness for common metric sizes (ISO 4032). */
export type HexNutSpec = {
  size: MetricScrewSize;
  acrossFlats: number;
  thickness: number;
  /** Recommended pocket clearance over acrossFlats for captive-nut traps. */
  pocketClearance: number;
};
export const HEX_NUTS: Record<MetricScrewSize, HexNutSpec> = {
  M2:    { size: 'M2',    acrossFlats: 4.0,  thickness: 1.6, pocketClearance: 0.2 },
  'M2.5':{ size: 'M2.5',  acrossFlats: 5.0,  thickness: 2.0, pocketClearance: 0.2 },
  M3:    { size: 'M3',    acrossFlats: 5.5,  thickness: 2.4, pocketClearance: 0.2 },
  M4:    { size: 'M4',    acrossFlats: 7.0,  thickness: 3.2, pocketClearance: 0.2 },
  M5:    { size: 'M5',    acrossFlats: 8.0,  thickness: 4.0, pocketClearance: 0.2 },
  M6:    { size: 'M6',    acrossFlats: 10.0, thickness: 5.2, pocketClearance: 0.2 },
  M8:    { size: 'M8',    acrossFlats: 13.0, thickness: 6.8, pocketClearance: 0.3 },
};

/** Heat-set insert (Voltive/E-Z LOK style) — nominal pilot, OD, length, and the
 *  surrounding boss the part needs to provide. Boss OD targets 2× pilot OD. */
export type HeatSetInsertSpec = {
  size: MetricScrewSize;
  pilotHole: number;
  insertOd: number;
  insertLength: number;
  bossOd: number;
  bossHeight: number;
  /** Lead-in chamfer at the boss top, mm. */
  leadInChamfer: number;
};
export const HEAT_SET_INSERTS: Record<'M2' | 'M3' | 'M4' | 'M5', HeatSetInsertSpec> = {
  M2: { size: 'M2', pilotHole: 3.2, insertOd: 4.0, insertLength: 4.0, bossOd: 5.6, bossHeight: 6.0, leadInChamfer: 0.4 },
  M3: { size: 'M3', pilotHole: 4.0, insertOd: 5.6, insertLength: 5.0, bossOd: 7.5, bossHeight: 8.0, leadInChamfer: 0.5 },
  M4: { size: 'M4', pilotHole: 5.5, insertOd: 6.4, insertLength: 8.1, bossOd: 8.8, bossHeight: 10.0, leadInChamfer: 0.5 },
  M5: { size: 'M5', pilotHole: 6.4, insertOd: 7.1, insertLength: 9.5, bossOd: 10.0, bossHeight: 12.0, leadInChamfer: 0.5 },
};

/** Common bearings (radial deep-groove). All dimensions in mm. */
export type BearingSpec = {
  designation: string;
  innerDiameter: number;
  outerDiameter: number;
  width: number;
};
export const BEARINGS: Record<string, BearingSpec> = {
  '608':  { designation: '608',  innerDiameter: 8,  outerDiameter: 22, width: 7 },
  '625':  { designation: '625',  innerDiameter: 5,  outerDiameter: 16, width: 5 },
  '6000': { designation: '6000', innerDiameter: 10, outerDiameter: 26, width: 8 },
  '6001': { designation: '6001', innerDiameter: 12, outerDiameter: 28, width: 8 },
  '6002': { designation: '6002', innerDiameter: 15, outerDiameter: 32, width: 9 },
};

/** Cup hinge cup dimensions (Blum/Salice 35mm standard). */
export type HingeCupSpec = {
  cupDiameter: 35;
  cupDepth: number;
  /** Distance from cup edge to door panel edge. */
  cupEdgeOffsetMin: number;
  /** Hinge plate mounting screws. */
  plateScrewSize: 'M3' | 'M4';
  plateScrewSpacing: number;
};
export const HINGE_CUP: HingeCupSpec = {
  cupDiameter: 35,
  cupDepth: 13,
  cupEdgeOffsetMin: 4,
  plateScrewSize: 'M4',
  plateScrewSpacing: 32,
};

/** Cable gland nominal hole sizes (PG and metric). */
export type CableGlandSpec = {
  threadDesignation: string;
  holeDiameter: number;
  flangeDiameter: number;
};
export const CABLE_GLANDS: Record<'M12' | 'M16' | 'M20', CableGlandSpec> = {
  M12: { threadDesignation: 'M12 × 1.5', holeDiameter: 12.5, flangeDiameter: 17.0 },
  M16: { threadDesignation: 'M16 × 1.5', holeDiameter: 16.5, flangeDiameter: 22.0 },
  M20: { threadDesignation: 'M20 × 1.5', holeDiameter: 20.5, flangeDiameter: 27.0 },
};

/** Keyway dimensions (DIN 6885A) by shaft diameter. */
export type KeywaySpec = {
  shaftDiameterMin: number;
  shaftDiameterMax: number;
  width: number;
  depth: number;
};
export const KEYWAYS: KeywaySpec[] = [
  { shaftDiameterMin: 6,  shaftDiameterMax: 8,  width: 2, depth: 1.2 },
  { shaftDiameterMin: 8,  shaftDiameterMax: 10, width: 3, depth: 1.8 },
  { shaftDiameterMin: 10, shaftDiameterMax: 12, width: 4, depth: 2.5 },
  { shaftDiameterMin: 12, shaftDiameterMax: 17, width: 5, depth: 3.0 },
  { shaftDiameterMin: 17, shaftDiameterMax: 22, width: 6, depth: 3.5 },
  { shaftDiameterMin: 22, shaftDiameterMax: 30, width: 8, depth: 4.0 },
];

/** Process-specific clearance/oversize adjustments, in mm.
 *  Builders apply these to nominal hole sizes when the manufacturing
 *  process is known. Negative values shrink shafts; positive values
 *  enlarge holes. */
export type ProcessAdjustments = {
  holeOversize: number;
  shaftUndersize: number;
  /** Minimum recommended wall thickness around a captive nut/insert. */
  insertWallMin: number;
};
export const PROCESS_ADJUSTMENTS: Record<'FDM' | 'SLA' | 'SLS' | 'CNC' | 'WOOD', ProcessAdjustments> = {
  FDM:  { holeOversize: 0.3, shaftUndersize: 0.2, insertWallMin: 1.6 },
  SLA:  { holeOversize: 0.1, shaftUndersize: 0.05, insertWallMin: 1.0 },
  SLS:  { holeOversize: 0.2, shaftUndersize: 0.15, insertWallMin: 1.2 },
  CNC:  { holeOversize: 0.05, shaftUndersize: 0.025, insertWallMin: 0.8 },
  WOOD: { holeOversize: 0.5, shaftUndersize: 0.0, insertWallMin: 4.0 },
};
